// src/pages/LegacyHome.jsx
import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import AppDataGrid from "../components/AppDataGrid";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise"; // master/detail
import DOMPurify from "dompurify";
import { BASE_URL } from "../utils";
import { FaPlus, FaMinus } from "react-icons/fa";
import { FiPaperclip, FiTrash2 } from "react-icons/fi";
import FamilyHistoryModal from "./FamilyHistoryModal"; // ✅ use the same working modal
import { Edit2Icon, RotateCcw } from "lucide-react";

// import "ag-grid-community/styles/ag-grid.css";
// import "ag-grid-community/styles/ag-theme-alpine.css";

// Register AG Grid modules (including MasterDetail)
ModuleRegistry.registerModules([AllCommunityModule, MasterDetailModule]);

// Portal modal renders into document.body to avoid stacking context issues
function PortalModal({ open, title, children, onClose }) {
  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded shadow-lg w-full max-w-4xl p-4 max-h-[80vh] overflow-auto"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <div>
            <button onClick={onClose} className="px-2 py-1 border rounded text-sm">
              Close
            </button>
          </div>
        </div>

        <div className="raw-html-preview prose max-w-full text-sm">{children}</div>
      </div>
    </div>,
    document.body
  );
}

function TabButton({ active, children, onClick, variant = "default" }) {
  const base =
    "h-8 px-3 rounded-full text-[12px] font-semibold border shadow-sm transition-colors";

  const styles = {
    default: active
      ? "bg-slate-900 text-white border-slate-900"
      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",

    green: active
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",

    red: active
      ? "bg-red-50 text-red-700 border-red-200"
      : "bg-white text-slate-700 border-slate-200 hover:bg-red-50",
  };

  return (
    <button type="button" onClick={onClick} className={`${base} ${styles[variant] || styles.default}`}>
      {children}
    </button>
  );
}


// Date formatting: MM/DD/YYYY (e.g. 12/09/2025)
function pad2(n) {
  return String(n).padStart(2, "0");
}
function formatDateMDY(dateLike) {
  if (!dateLike && dateLike !== 0) return null;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/* =========================================================
   ✅ Address truncate + tooltip cell (IMPROVED UI)
   ========================================================= */
const truncate = (s, n = 20) => {
  if (s == null) return "";
  const str = String(s).trim();
  if (!str) return "";
  return str.length > n ? str.slice(0, n) : str;
};

const AddressCell = (props) => {
  const full = props.value == null ? "" : String(props.value).trim();
  const isLong = full.length > 20;
  const short = truncate(full, 20);

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 320 });

  React.useEffect(() => {
    if (!open) return;

    const updatePos = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({
        top: r.bottom + 8,
        left: Math.min(r.left, window.innerWidth - 460),
        width: Math.min(420, Math.max(260, r.width)),
      });
    };

    updatePos();

    const onDocMouseDown = (e) => {
      const el = anchorRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!full) return <span className="text-slate-400">—</span>;

  return (
    <div ref={anchorRef} className="w-full">
      <div className="flex items-center gap-2 w-full">
        <span className="block flex-1 min-w-0 truncate text-sm" style={{ userSelect: "text" }}>
          {isLong ? `${short}…` : full}
        </span>

        {isLong && (
          <button
            type="button"
            className="shrink-0 text-[11px] text-blue-600 underline hover:text-blue-700"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            {open ? "Hide" : "View more"}
          </button>
        )}
      </div>

      {open &&
        ReactDOM.createPortal(
          <div
            className="fixed z-[999999]"
            style={{ top: pos.top, left: pos.left }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="rounded-lg border border-slate-200 bg-white shadow-xl p-2 text-xs text-slate-800 whitespace-normal break-words w-[420px] max-w-[calc(100vw-24px)]">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="font-semibold text-slate-700">Full Address</div>
                <button
                  className="text-[11px] text-slate-500 hover:text-slate-800"
                  onClick={() => setOpen(false)}
                  type="button"
                >
                  ✕
                </button>
              </div>
              <div style={{ userSelect: "text" }}>{full}</div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

/* =========================================================
   ✅ Latest note helpers
   ========================================================= */
function noteTimeMs(note) {
  const v = note?.created_at || note?.updated_at || note?.timestamp || null;
  const d = v ? new Date(v) : null;
  const ms = d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  return ms;
}

function getLatestNote(row) {
  const notes = Array.isArray(row?.notes) ? row.notes : [];
  if (notes.length === 0) return null;
  const sorted = [...notes].sort((a, b) => noteTimeMs(a) - noteTimeMs(b));
  return sorted[sorted.length - 1] || null;
}

/* =========================================================
   ✅ Status badge (works in light + dark)
   ========================================================= */
function StatusPill({ value }) {
  const raw = (value ?? "").toString().trim();
  const v = raw ? raw : "NA";
  const key = v.toLowerCase();

  const base = "status-pill";
  const cls =
    key === "approved"
      ? "status-pill--approved"
      : key === "processing" || key === "in progress"
      ? "status-pill--processing"
      : key === "pending"
      ? "status-pill--pending"
      : key === "rejected"
      ? "status-pill--rejected"
      : "status-pill--na";

  return <span className={`${base} ${cls}`}>{v}</span>;
}

export default function LegacyHome() {
  const gridRef = useRef(null);
  const mountedRef = useRef(true);

  // ✅ Tabs + selection
  const [tab, setTab] = useState("active"); // 'active' | 'deleted'
  const [selectedIds, setSelectedIds] = useState([]);

  const [rowData, setRowData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [availableColumns, setAvailableColumns] = useState([]);
  const [quickFilter, setQuickFilter] = useState("");
  const [pageInput, setPageInput] = useState("");

  // responsive grid height (calculated from viewport)
  const [gridHeight, setGridHeight] = useState(Math.max(320, window.innerHeight - 200));

  // html modal state
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlModalContent, setHtmlModalContent] = useState("");
  const [htmlModalTitle, setHtmlModalTitle] = useState("Family History — HTML View");

  // edit-note modal state (shared editor)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const editEditorRef = useRef(null);
  const [editSaving, setEditSaving] = useState(false);

  // ✅ Edit Family modal
  const [familyModalOpen, setFamilyModalOpen] = useState(false);
  const [familyModalRecord, setFamilyModalRecord] = useState(null);

  // ✅ Delete record modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetRow, setDeleteTargetRow] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // ✅ Restore state
  const [restoreBusy, setRestoreBusy] = useState(false);

  useEffect(() => {
    function onResize() {
      setGridHeight(Math.max(320, window.innerHeight - 200));
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // helpers
  function escapeHtml(unsafe) {
    if (!unsafe) return "";
    return String(unsafe)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
  function stripHtml(html) {
    if (!html) return "";
    return String(html).replace(/<[^>]*>/g, "");
  }

  // showHtmlContent - stable callback to sanitize + open modal
  const showHtmlContent = useCallback((html, title = "HTML View") => {
    if (!html) return;
    let safe = String(html || "");
    safe = safe.replace(/<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
    safe = safe.replace(/\son\w+=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
    try {
      safe = DOMPurify.sanitize(safe, { ADD_ATTR: ["target", "class", "style", "data-*"] });
    } catch (e) {
      safe = escapeHtml(stripHtml(safe));
    }
    setHtmlModalContent(safe);
    setHtmlModalTitle(title);
    setHtmlModalOpen(true);
  }, []);

  // ---------------- Notes UI components ----------------
  const buildPdfHref = (pdf_url) => {
    if (!pdf_url) return null;
    if (pdf_url.startsWith("http://") || pdf_url.startsWith("https://")) return pdf_url;
    const path = pdf_url.startsWith("/") ? pdf_url : `/${pdf_url}`;
    return `${BASE_URL}${path}`;
  };

  function NoteItem({ note, caseId, onUpdate, onDelete }) {
    const [isEditingInline, setIsEditingInline] = useState(false);
    const [editedText, setEditedText] = useState(note.note || note.text || "");

    useEffect(() => {
      setEditedText(note.note || note.text || "");
    }, [note.note, note.text]);

    const openEditModal = () => {
      setEditingNote({ ...note, caseId });
      setEditModalOpen(true);
      setTimeout(() => {
        if (editEditorRef.current) {
          editEditorRef.current.innerHTML = note.note || note.text || "";
          editEditorRef.current.focus();
        }
      }, 60);
    };

    const pdfHref = buildPdfHref(note.pdf_url || note.pdfUrl || note.url || "");

    return (
      <div
        className={`text-sm mb-3 pl-2 border-l-4 ${
          note.status === "Approved" ? "border-green-500" : "border-gray-300"
        }`}
      >
        {isEditingInline ? (
          <>
            <textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              className="text-xs border px-2 py-1 rounded w-full"
            />
            <div className="flex gap-2 mt-1">
              <button
                onClick={async () => {
                  if (!editedText.trim()) return alert("Note cannot be empty!");
                  if (onUpdate) await onUpdate(editedText.trim());
                  setIsEditingInline(false);
                }}
                className="text-green-600 text-xs"
              >
                💾 Save
              </button>
              <button onClick={() => setIsEditingInline(false)} className="text-gray-500 text-xs">
                ❌ Cancel
              </button>

              <button onClick={openEditModal} className="text-indigo-600 text-xs ml-auto">
                Open Editor
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              className="text-gray-800 whitespace-pre-line mb-2"
              dangerouslySetInnerHTML={{ __html: note.note || note.text || "" }}
            />
            {pdfHref && (
              <div className="mb-2">
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 underline inline-flex items-center gap-1"
                >
                  <FiPaperclip size={12} /> View Attached PDF
                </a>
              </div>
            )}
            <div className="text-[11px] text-gray-500 flex items-center justify-between">
              <span>{note.created_at ? new Date(note.created_at).toLocaleString() : note.timestamp || ""}</span>
              <div className="flex gap-3 items-center">
                <button onClick={() => setIsEditingInline(true)} className="text-indigo-600 text-xs">
                  ✏️ Edit
                </button>
                <button onClick={() => onDelete && onDelete()} className="text-red-500 text-xs">
                  <FiTrash2 size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ---------------- Data fetching ----------------
  const fetchData = useCallback(
    async (p = 1, pageSize = limit) => {
      try {
        setLoading(true);
        gridRef.current?.api?.showLoadingOverlay?.();

        const res = await axios.get(`${BASE_URL}/api/legacy`, {
          params: {
            page: p,
            limit: pageSize,
            search: search || undefined,
            tab,
          },
        });

        if (!mountedRef.current) return;

        const rows = res.data?.data || [];
        setRowData(rows);
        setTotal(res.data?.meta?.total || 0);
        setPage(res.data?.meta?.page || p);

        // reset selection after reload
        try {
          gridRef.current?.api?.deselectAll?.();
        } catch (e) {}
        setSelectedIds([]);

        try {
          gridRef.current?.api?.sizeColumnsToFit?.();
        } catch (e) {}

        setTimeout(() => gridRef.current?.api?.hideOverlay?.(), 80);
      } catch (err) {
        console.error("fetch legacy", err);
        try {
          gridRef.current?.api?.showNoRowsOverlay?.();
        } catch (e) {}
      } finally {
        setLoading(false);
      }
    },
    [limit, search, tab]
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchData(1, limit);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData, limit]);

  // refetch on tab change
  useEffect(() => {
    setPage(1);
    fetchData(1, limit);
  }, [tab]); 

  // bulk delete selected (Active tab)
  const bulkDeleteSelected = useCallback(async () => {
    if (tab !== "active") return;
    if (!selectedIds.length) return;

    if (!window.confirm(`Delete ${selectedIds.length} record(s)?`)) return;

    try {
      await axios.post(`${BASE_URL}/api/legacy/bulk-delete`, { ids: selectedIds });

      try {
        gridRef.current?.api?.deselectAll?.();
      } catch (e) {}
      setSelectedIds([]);
      fetchData(page, limit);
    } catch (err) {
      console.error("bulk delete error", err);
      alert(err?.response?.data?.message || "Failed to delete selected records");
    }
  }, [selectedIds, tab, page, limit, fetchData]);

  // bulk restore selected (Deleted tab)
  const bulkRestoreSelected = useCallback(async () => {
    if (tab !== "deleted") return;
    if (!selectedIds.length) return;

    if (!window.confirm(`Restore ${selectedIds.length} record(s)?`)) return;

    setRestoreBusy(true);
    try {
      await axios.post(`${BASE_URL}/api/legacy/bulk-restore`, { ids: selectedIds });

      try {
        gridRef.current?.api?.deselectAll?.();
      } catch (e) {}
      setSelectedIds([]);
      fetchData(page, limit);
    } catch (err) {
      console.error("bulk restore error", err);
      alert(err?.response?.data?.message || "Failed to restore selected records");
    } finally {
      setRestoreBusy(false);
    }
  }, [selectedIds, tab, page, limit, fetchData]);

  // DetailCellRenderer used by master/detail
  const DetailCellRenderer = (props) => {
    const row = props.data || {};
    const notes = Array.isArray(row.notes) ? row.notes : [];
    const latest = getLatestNote(row);

    const latestStatus = latest?.status || "NA";
    const latestSubStatus = latest?.sub_status || latest?.subStatus || "NA";
    const latestLabel = latest?.label || "NA";
    const latestFollowUp = latest?.followUp_date || latest?.followup_date || latest?.followUpDate || null;

    const deleteNote = async (note) => {
      const nid = note.id || note.noteId || note.note_id;
      if (!nid) return;
      if (!window.confirm("Delete this note?")) return;
      try {
        await axios.delete(`${BASE_URL}/api/legacy/note/${nid}`).catch(async () => {
          await axios.delete(`${BASE_URL}/api/legacy/${row.id || row.legacy_id || row.case_id}/notes/${nid}`);
        });
        fetchData(page, limit);
      } catch (err) {
        console.error("delete note error", err);
        alert("Failed to delete note");
      }
    };

    const updateNote = async (note, newText) => {
      const nid = note.id || note.noteId || note.note_id;
      if (!nid) return;
      try {
        await axios.put(`${BASE_URL}/api/legacy/${row.id || row.legacy_id || row.case_id}/notes/${nid}`, {
          note: newText,
          plain_text: stripHtml(newText),
        });
        await fetchData(page, limit);
      } catch (err) {
        console.error("update note error", err);
        alert("Failed to update note");
      }
    };

    return (
      <div className="p-2 text-xs bg-[var(--surface-muted)]" style={{ whiteSpace: "pre-wrap", overflow: "visible" }}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <div className="flex items-center gap-2">
            <strong>Status:</strong> <StatusPill value={latestStatus} />
          </div>
          <div>
            <strong>Sub-Status:</strong> {latestSubStatus || "NA"}
          </div>
          <div>
            <strong>Label:</strong> {latestLabel || "NA"}
          </div>
          <div>
            <strong>Follow Up:</strong> {latestFollowUp ? formatDateMDY(latestFollowUp) || "—" : "—"}
          </div>
        </div>

        <div>
          <strong>Notes:</strong>
        </div>
        <div className="mt-1">
          {notes.length === 0 && <div className="text-xs text-gray-500">No notes</div>}
          {notes.map((n) => (
            <NoteItem
              key={n.id || n.noteId || n.timestamp || Math.random()}
              note={n}
              caseId={row.id}
              onUpdate={(newText) => updateNote(n, newText)}
              onDelete={() => deleteNote(n)}
            />
          ))}
        </div>
      </div>
    );
  };

  // Column definitions
  const renderTextOrDash = (val) => {
    if (val === null || val === undefined) return "—";
    const s = String(val).trim();
    return s === "" ? "—" : s;
  };

  const buildCaseId = (data) => {
    if (!data) return "—";
    const b = data.property_borough ?? data.property_borough_name ?? "";
    const block = data.property_block ?? "";
    const lot = data.property_lot ?? "";
    if (![b, block, lot].some((x) => x !== null && x !== undefined && String(x).trim() !== "")) {
      return "—";
    }
    const parts = [b, block, lot].map((p) => (p === null || p === undefined ? "" : String(p).trim()));
    return `${parts[0] || ""}-${parts[1] || ""}-${parts[2] || ""}`.replace(/^-+|-+$/g, "").replace(/-{2,}/g, "-");
  };

  // open family editor
  const openFamilyEditor = useCallback((row) => {
    setFamilyModalRecord(row || null);
    setFamilyModalOpen(true);
  }, []);

  // delete confirm modal (active tab only)
  const openDeleteConfirm = useCallback(
    (row) => {
      if (tab !== "active") return;
      setDeleteTargetRow(row || null);
      setDeleteModalOpen(true);
    },
    [tab]
  );

  // restore single (deleted tab only)
  const restoreSingle = useCallback(
    async (row) => {
      if (tab !== "deleted") return;
      const id = row?.id;
      if (!id) return;

      if (!window.confirm("Restore this record?")) return;

      setRestoreBusy(true);
      try {
        await axios.put(`${BASE_URL}/api/legacy/${id}/restore`);
        await fetchData(page, limit);
      } catch (err) {
        console.error("restore single error", err);
        alert(err?.response?.data?.message || "Failed to restore record");
      } finally {
        setRestoreBusy(false);
      }
    },
    [tab, page, limit, fetchData]
  );

  // delete record (soft delete via backend)
  const confirmDeleteRecord = useCallback(async () => {
    const id = deleteTargetRow?.id;
    if (!id) return;
    setDeleteBusy(true);

    try {
      await axios.delete(`${BASE_URL}/api/legacy/${id}`);
      setDeleteModalOpen(false);
      setDeleteTargetRow(null);

      await fetchData(page, limit);
    } catch (err) {
      console.error("delete legacy record error", err);
      alert(err?.response?.data?.message || "Failed to delete record");
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteTargetRow, page, limit, fetchData]);

  const columnDefs = useMemo(() => {
    return [
      // Selection column (Active + Deleted)
      {
        headerName: "",
        field: "__select",
        width: 46,
        minWidth: 46,
        maxWidth: 46,
        pinned: "left",
        sortable: false,
        filter: false,
        suppressMenu: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        resizable: false,
        cellClass: "text-center",
      },

      {
        headerName: "Case id",
        field: "case_id",
        width: 120,
        minWidth: 120,
        sortable: false,
        valueGetter: (p) => buildCaseId(p.data),
        cellRenderer: (params) => {
          const val = buildCaseId(params.data);
          if (!params.data || val === "—") return "—";
          const id = params.data?.id || "";
          const href = id ? `/legacy/${id}` : "#";
          return (
            <a href={href} target="_blank" rel="noreferrer" className="text-blue-600 underline" title="Open details">
              {val}
            </a>
          );
        },
      },

      {
        headerName: "Full Name",
        field: "full_name",
        width: 160,
        minWidth: 160,
        tooltipField: "full_name",
        cellRenderer: (params) => {
          const name = renderTextOrDash(params.value);
          if (name === "—") return "—";
          return <span className="text-sm">{String(name)}</span>;
        },
      },

      {
        headerName: "Date of death",
        field: "date_of_death",
        width: 140,
        valueGetter: (p) => p.data?.obituary_published_date || null,
        cellRenderer: (p) => {
          if (!p.value) return "—";
          const f = formatDateMDY(p.value);
          return f || "—";
        },
      },

      {
        headerName: "Address",
        field: "property_full_address",
        width: 220,
        minWidth: 220,
        cellRenderer: AddressCell,
      },

      {
        headerName: "City",
        field: "property_city",
        width: 100,
        minWidth: 100,
        cellRenderer: (p) => renderTextOrDash(p.value),
      },

      // STATUS (from latest note)
      {
        headerName: "Status",
        field: "__latest_status",
        width: 120,
        minWidth: 120,
        sortable: true,
        valueGetter: (p) => getLatestNote(p.data)?.status || "NA",
        cellRenderer: (p) => <StatusPill value={p.value} />,
        tooltipValueGetter: (p) => getLatestNote(p.data)?.status || "NA",
      },

      {
        headerName: "Family History (html)",
        field: "family_history_html",
        minWidth: 140,
        width: 200,
        cellRenderer: (params) => {
          const htmlVal = params.value ?? "";
          const raw = String(htmlVal).trim();
          const hasHtml = raw && raw.toLowerCase() !== "na";

          if (hasHtml) {
            return (
              <button
                className="p-1 rounded hover:bg-gray-100 ag-icon-btn text-blue-600"
                title="View HTML"
                aria-label="View HTML"
                onClick={(e) => {
                  e.stopPropagation();
                  showHtmlContent(htmlVal, `Family History — ${params.data?.full_name || ""}`);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </button>
            );
          }
          return renderTextOrDash(null);
        },
      },

      {
        headerName: "Block",
        field: "property_block",
        width: 100,
        minWidth: 100,
        cellRenderer: (p) => renderTextOrDash(p.value),
      },

      {
        headerName: "Lot",
        field: "property_lot",
        width: 90,
        minWidth: 90,
        cellRenderer: (p) => renderTextOrDash(p.value),
      },

      {
        headerName: "State",
        field: "property_state",
        width: 120,
        cellRenderer: (p) => {
          const v = p.value ?? p.data?.property_state ?? "";
          const t = String(v).trim();
          return t === "" ? "New York" : t;
        },
      },

      {
        headerName: "Notes",
        field: "notes",
        width: 100,
        minWidth: 100,
        cellRenderer: (params) => {
          const notes = Array.isArray(params.data?.notes) ? params.data.notes : [];
          const count = notes.length;
          const isExpanded = !!params.node?.expanded;
          return (
            <div className="w-full flex items-center justify-center">
              <div className="flex items-center gap-2">
                <span className="text-xs">{count > 0 ? `${count}` : "0"}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    try {
                      const node = params.node;
                      const willOpen = !node.expanded;
                      params.api.forEachNode((n) => {
                        if (n && n.expanded) n.setExpanded(false);
                      });
                      node.setExpanded(willOpen);
                      params.api.refreshCells({ force: true });
                      params.api.resetRowHeights();
                    } catch (err) {
                      console.warn("toggle expand failed", err);
                    }
                  }}
                  title={isExpanded ? "Collapse" : "Expand"}
                  className={`w-5 h-5 rounded-full flex items-center justify-center shadow-md border ${
                    isExpanded ? "bg-red-500 text-white" : "bg-blue-600 text-white"
                  }`}
                >
                  {isExpanded ? <FaMinus size={10} /> : <FaPlus size={10} />}
                </button>
              </div>
            </div>
          );
        },
      },

      {
        headerName: "Created on",
        field: "created_at",
        width: 120,
        cellRenderer: (p) => {
          if (!p.value) return "—";
          const f = formatDateMDY(p.value);
          return f || "—";
        },
      },

      // Actions: Edit + Delete (Active) OR Restore (Deleted)
      {
        headerName: "Actions",
        field: "__actions",
        width: 120,
        minWidth: 120,
        sortable: false,
        filter: false,
        cellRenderer: (params) => {
          const row = params.data;
          if (!row?.id) return "—";

          return (
            <div className="flex items-center justify-center gap-2">
              <button
                className="ui-btn"
                title="Edit Family"
                onClick={(e) => {
                  e.stopPropagation();
                  openFamilyEditor(row);
                }}
              >
                <Edit2Icon size={14} />
              </button>

              {tab === "active" ? (
                <button
                  className="p-1 rounded border border-red-200 hover:bg-red-50 text-red-600"
                  title="Delete Record"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteConfirm(row);
                  }}
                >
                  <FiTrash2 size={14} />
                </button>
              ) : (
                <button
                  className="ui-btn"
                  title="Restore Record"
                  onClick={(e) => {
                    e.stopPropagation();
                    restoreSingle(row);
                  }}
                  disabled={restoreBusy}
                >
                  <RotateCcw size={14} />
                </button>
              )}
            </div>
          );
        },
      },
    ];
  }, [showHtmlContent, openFamilyEditor, openDeleteConfirm, restoreSingle, tab, restoreBusy]);

  // onGridReady
  function onGridReady(params) {
    try {
      if (!gridRef.current) gridRef.current = {};
      gridRef.current.api = params.api;
      gridRef.current.columnApi = params.columnApi;
    } catch (e) {}

    setTimeout(() => {
      try {
        const columnApi = params?.columnApi || gridRef.current?.columnApi;
        if (!columnApi) {
          setAvailableColumns([]);
          return;
        }

        const allCols = columnApi.getAllGridColumns ? columnApi.getAllGridColumns() : columnApi.getAllColumns?.() || [];
        allCols.forEach((col, idx) => {
          const pinned = !!(col.getColDef && col.getColDef().pinned);
          const shouldShow = pinned || idx < 6;
          try {
            columnApi.setColumnVisible(col.getColId(), shouldShow);
          } catch (err) {}
        });

        const mapped = (allCols || []).map((c) => ({
          colId: c.getColId(),
          headerName: (c.getColDef && c.getColDef().headerName) || c.getColId(),
        }));
        setAvailableColumns(mapped);
      } catch (err) {
        console.warn("onGridReady read columns failed:", err);
        setAvailableColumns([]);
      }
    }, 50);
  }

  // handle clicks fallback (family history html open)
  const onCellClicked = (params) => {
    try {
      if (!params || !params.colDef) return;

      if (params.colDef.field === "family_history_html" || params.colDef.field === "family_history") {
        const raw = params.node?.data?.family_history_html || params.value || "";
        const trimmed = String(raw).trim();
        const hasHtml = trimmed && trimmed.toLowerCase() !== "na";

        if (hasHtml) {
          let safe = String(raw);

          // remove scripts/styles + inline handlers
          safe = safe.replace(/<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
          safe = safe.replace(/\son\w+=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");

          // ADD NUMBERING (convert UL to OL)
          try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(safe, "text/html");

            const uls = doc.querySelectorAll("ul");
            uls.forEach((ul) => {
              const ol = doc.createElement("ol");

              for (const attr of ul.attributes) {
                ol.setAttribute(attr.name, attr.value);
              }

              const existingStyle = ol.getAttribute("style") || "";
              ol.setAttribute("style", `${existingStyle}; list-style-type: decimal; padding-left: 22px; margin: 6px 0;`);

              while (ul.firstChild) ol.appendChild(ul.firstChild);

              ul.replaceWith(ol);
            });

            safe = doc.body.innerHTML;
          } catch (e) {}

          // sanitize after numbering transform
          try {
            safe = DOMPurify.sanitize(safe, {
              ADD_ATTR: ["target", "class", "style", "data-*"],
            });
          } catch (e) {}

          setHtmlModalContent(safe);
          setHtmlModalTitle(`Family History — ${params.data?.full_name || ""}`);
          setHtmlModalOpen(true);
        }
      }
    } catch (e) {
      console.warn("onCellClicked error", e);
    }
  };

  const onExportCsv = () => {
    gridRef.current?.api?.exportDataAsCsv({
      fileName: `legacy-obits-page-${page}.csv`,
      allColumns: false,
    });
  };

  // Pagination helpers
  const totalPages = Math.max(1, Math.ceil(total / limit || 1));
  const startRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const endRow = Math.min(total, page * limit);

  const goFirst = () => {
    if (page === 1) return;
    fetchData(1, limit);
  };
  const goPrev = () => {
    if (page <= 1) return;
    fetchData(Math.max(1, page - 1), limit);
  };
  const goNext = () => {
    if (page >= totalPages) return;
    fetchData(Math.min(totalPages, page + 1), limit);
  };
  const goLast = () => {
    if (page === totalPages) return;
    fetchData(totalPages, limit);
  };
  const goPage = (p) => {
    const np = Math.max(1, Math.min(totalPages, p));
    fetchData(np, limit);
  };

  const onSelectionChanged = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const nodes = api.getSelectedNodes ? api.getSelectedNodes() : [];
    const ids = (nodes || [])
      .map((n) => n?.data?.id)
      .filter((x) => x !== null && x !== undefined)
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n));
    setSelectedIds([...new Set(ids)]);
  }, []);

  function renderPageButtons() {
    const maxButtons = 3;
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    const buttons = [];
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => goPage(i)}
          className={`px-3 py-1 border rounded text-sm ${i === page ? "bg-blue-600 text-white" : "bg-white"}`}
          aria-current={i === page ? "page" : undefined}
        >
          {i}
        </button>
      );
    }
    return buttons;
  }

  const changePageSize = (size) => {
    setLimit(size);
    setPage(1);
    fetchData(1, size);
  };

  const handleJumpSubmit = (e) => {
    e.preventDefault();
    const n = Number(pageInput);
    if (!n || n < 1) return;
    goPage(n);
    setPageInput("");
  };

  const selectedCount = selectedIds.length;

  return (
    <div className="p-4 flex flex-col min-h-[80vh]">
      {/* Toolbar card */}
      <div className="filter-panel mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="min-w-0">
              <div className="page-kicker">Legacy</div>
              <h2 className="page-title truncate">Legacy Home</h2>
              <div className="page-subtitle">Legacy obits</div>

              {/* ✅ Move tabs here (as per screenshot) */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <div className="text-sm font-semibold text-slate-800">Results</div>

                <TabButton
                  active={tab === "active"}
                  variant="green"
                  onClick={() => setTab("active")}
                >
                  Active
                </TabButton>

                <TabButton
                  active={tab === "deleted"}
                  variant="red"
                  onClick={() => setTab("deleted")}
                >
                  Deleted
                </TabButton>

                {tab === "deleted" ? (
                  <div className="ml-1 text-[12px] text-slate-500">
                    Showing soft-deleted records
                  </div>
                ) : null}
              </div>

            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap md:flex-nowrap md:ml-4">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Server search: name / doc id / party"
              className="ui-input w-full md:w-56"
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchData(1, limit);
              }}
            />

            <button
              onClick={() => fetchData(1, limit)}
              className="ui-btn ui-btn-primary"
              disabled={loading}
              title="Search on server"
            >
              Search
            </button>

            <button
              onClick={onExportCsv}
              className="ui-btn"
              title="Export current view as CSV"
            >
              Export
            </button>

            <select
              value={limit}
              onChange={(e) => changePageSize(Number(e.target.value))}
              className="ui-select"
              title="Rows per page"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            {/* ✅ contextual bulk action button */}
            {tab === "active" && selectedCount > 0 ? (
              <button
                onClick={bulkDeleteSelected}
                className="ui-btn ui-btn-danger"
                title="Delete selected"
              >
                Delete ({selectedCount})
              </button>
            ) : null}

            {tab === "deleted" && selectedCount > 0 ? (
              <button
                onClick={bulkRestoreSelected}
                disabled={restoreBusy}
                className="ui-btn ui-btn-primary disabled:opacity-60"
                title="Restore selected"
              >
                {restoreBusy ? "Restoring..." : `Restore (${selectedCount})`}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Grid wrapper */}
      <div className="rounded-lg border flex-1 bg-white overflow-hidden">
        <AppDataGrid
          gridRef={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
            minWidth: 80,
            cellClass: "text-sm text-center",
            headerClass: "text-center",
            cellStyle: { userSelect: "text" },
          }}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          suppressRowClickSelection={true}
          rowHeight={42}
          headerHeight={36}
          rowSelection="multiple"
          onSelectionChanged={onSelectionChanged}
          animateRows={true}
          onGridReady={onGridReady}
          onCellClicked={onCellClicked}
          overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading data...</span>'
          overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No records to display</span>'
          masterDetail={true}
          isRowMaster={() => true}
          detailCellRenderer={DetailCellRenderer}
          detailRowAutoHeight={true}
          detailRowHeight={220}
          pagination={false}
          domLayout="normal"
          getRowId={(params) => (params.data && params.data.id ? params.data.id : `${Math.random()}`)}
          loading={loading}
          height={gridHeight}
          minWidth={900}
        />
      </div>

      {/* Pagination */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-3 mt-4">
        <div className="text-sm text-slate-600">
          {total === 0 ? (
            <>No records</>
          ) : (
            <>
              Showing <strong>{startRow}</strong>–<strong>{endRow}</strong> of <strong>{total}</strong>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={goFirst} disabled={page <= 1} className="px-3 py-1 border rounded disabled:opacity-60 text-sm">
            « First
          </button>
          <button onClick={goPrev} disabled={page <= 1} className="px-3 py-1 border rounded disabled:opacity-60 text-sm">
            ◀ Prev
          </button>

          <div className="flex items-center gap-1">{renderPageButtons()}</div>

          <button
            onClick={goNext}
            disabled={page >= totalPages}
            className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
          >
            Next ▶
          </button>
          <button
            onClick={goLast}
            disabled={page >= totalPages}
            className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
          >
            Last »
          </button>

          <div className="ml-2 text-sm text-slate-600">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </div>

          <form onSubmit={handleJumpSubmit} className="flex items-center gap-1 ml-2">
            <input
              type="number"
              min="1"
              max={totalPages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              placeholder="Go to"
              className="ui-input w-20"
            />
            <button type="submit" className="px-3 py-1 border rounded text-sm">
              Go
            </button>
          </form>
        </div>
      </div>

      {/* Edit Note Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded shadow-lg w-full max-w-3xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Edit Note</h3>
              <div className="text-xs text-gray-500">
                {editingNote?.created_at ? new Date(editingNote.created_at).toLocaleString() : ""}
              </div>
            </div>

            <div
              ref={editEditorRef}
              contentEditable
              suppressContentEditableWarning
              className="border rounded p-2 mb-3 text-sm"
              style={{ minHeight: 180, maxHeight: 400, overflowY: "auto" }}
            />

            {editingNote?.pdf_url || editingNote?.pdfUrl ? (
              <div className="mb-3">
                <a
                  href={buildPdfHref(editingNote.pdf_url || editingNote.pdfUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline text-sm"
                >
                  <FiPaperclip size={14} /> View Attached PDF
                </a>
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setEditingNote(null);
                }}
                className="px-3 py-1 border rounded text-sm"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!editingNote) return;
                  const nid = editingNote.id || editingNote.noteId || editingNote.note_id;
                  if (!nid) return alert("No note id to save.");
                  const html = editEditorRef.current ? editEditorRef.current.innerHTML : editingNote.note || "";
                  if (!html || html.trim() === "") return alert("Note cannot be empty.");
                  setEditSaving(true);
                  try {
                    await axios.put(
                      `${BASE_URL}/api/legacy/${editingNote.caseId || editingNote.case_id || "unknown"}/notes/${nid}`,
                      {
                        note: html,
                        plain_text: stripHtml(html),
                      }
                    );
                    setEditModalOpen(false);
                    setEditingNote(null);
                    await fetchData(page, limit);
                  } catch (err) {
                    console.error("save edited note error", err);
                    alert("Failed to save note.");
                  } finally {
                    setEditSaving(false);
                  }
                }}
                disabled={editSaving}
                className={`px-4 py-2 rounded text-white ${editSaving ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"}`}
              >
                {editSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared Family editor modal */}
      <FamilyHistoryModal
        open={familyModalOpen}
        record={familyModalRecord}
        onClose={() => {
          setFamilyModalOpen(false);
          setFamilyModalRecord(null);
        }}
        onSaved={() => fetchData(page, limit)}
      />

      {/* Delete Record Confirm Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded shadow-lg w-full max-w-md p-4">
            <h3 className="text-lg font-semibold mb-2">Delete Record</h3>
            <div className="text-sm text-slate-700 mb-3">
              Are you sure you want to delete this record?
              <div className="mt-2 text-xs text-slate-500">
                {deleteTargetRow?.full_name ? (
                  <div>
                    <strong>Name:</strong> {deleteTargetRow.full_name}
                  </div>
                ) : null}
                {deleteTargetRow?.id ? (
                  <div>
                    <strong>ID:</strong> {deleteTargetRow.id}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDeleteTargetRow(null);
                }}
                className="px-3 py-1 border rounded text-sm"
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteRecord}
                className={`px-4 py-2 rounded text-white ${deleteBusy ? "bg-gray-400" : "bg-red-600 hover:bg-red-700"}`}
                disabled={deleteBusy}
              >
                {deleteBusy ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Family history HTML viewer */}
      <PortalModal open={htmlModalOpen} title={htmlModalTitle} onClose={() => setHtmlModalOpen(false)}>
        <div dangerouslySetInnerHTML={{ __html: htmlModalContent }} />
      </PortalModal>
    </div>
  );
}
